/**
 * Composition and serialization helpers over the theme token data.
 *
 * `foldLayers` is the single home of the stacking rules, `mergeTheme`
 * resolves the fold against a complete base (the provider's job — both are
 * deliberately off the barrel), and `tokensToCssVars` serializes a resolved
 * theme for the CSS-custom-property surface. They live apart from `tokens.ts`
 * so that module stays pure data, safe for token-only consumers like the
 * Preact teaser.
 */

import { toHoverColor, toPressColor } from './color.helpers';
import type { PartialTheme, Theme, ThemeLayer } from './tokens';
import { fontTokensFor } from './tokens';

/**
 * The stacking rules, in one place. Layers apply in order, later ones winning,
 * and falsy layers are skipped. Groups combine differently, and deliberately:
 *
 * - `colors` merges per token, so a layer overrides only what it names.
 * - `avatarColors` is replaced whole: it's an ordered list, not a bag of keys.
 * - `fonts` is a whole `FontGroup`, replaced whole. A layer that names a
 *   typeface brings its loader and displaces the group beneath, so the
 *   displaced group stops loading; a layer that names none leaves it alone.
 *
 * There is a writer for every `PartialTheme` group, checked against that type,
 * so a new group fails to compile here until the fold says how it stacks.
 */
export function foldLayers(layers: readonly ThemeLayer[]): PartialTheme {
    const composed: PartialTheme = {};
    const writeGroup: { [G in keyof Required<PartialTheme>]: (value: NonNullable<PartialTheme[G]>) => void } = {
        colors: (colors) => {
            composed.colors = { ...composed.colors, ...colors };
        },
        avatarColors: (avatarColors) => {
            composed.avatarColors = avatarColors;
        },
        fonts: (fonts) => {
            composed.fonts = fonts;
        },
    };
    for (const layer of layers) {
        if (!layer) continue;
        for (const group of Object.keys(writeGroup) as (keyof PartialTheme)[]) {
            const value = layer[group];
            // TS can't pair each group with its value type in this loop.
            if (value) (writeGroup[group] as (value: unknown) => void)(value);
        }
    }
    return composed;
}

/**
 * Applies a partial override onto a complete base theme, yielding a complete
 * one.
 *
 * Shares its group semantics with `foldLayers` by delegating to it. `base`
 * supplies every token, so the result is complete wherever the override is
 * partial and no group needs asserting back into shape.
 *
 * This is also where a `FontGroup` becomes readable tokens: the winning group's
 * family stacks are generated here.
 *
 * Deliberately absent from the `@repo/theme` barrel: `ThemeProvider` is the
 * only intended caller (it alone knows the correct base — its parent context).
 * Callers pass the provider their layers and let it resolve.
 */
export function mergeTheme(base: Theme, ...overrides: ThemeLayer[]): Theme {
    if (!overrides.some(Boolean)) return base;
    const layer = foldLayers(overrides);
    return {
        colors: { ...base.colors, ...layer.colors },
        spacing: base.spacing,
        fonts: layer.fonts ? fontTokensFor(layer.fonts) : base.fonts,
        avatarColors: layer.avatarColors ?? base.avatarColors,
    };
}

/**
 * Converts a resolved theme into a CSS custom property map for consumption by
 * CSS Modules via `var()`. Names match the legacy `:root` injection exactly:
 * `--{colorKey}`, `--spacing-{key}` (with a `px` suffix), and `--{fontKey}`.
 * Avatar colors are not emitted — they have no CSS consumers.
 *
 * Hover and press variants of `colorSurfacePrimary` are derived rather than
 * declared as tokens, because CSS Modules cannot call the derivation helpers
 * themselves. The design has no dedicated state colors, and predeclaring a
 * variant for every surface token would be a large surface for two consumers.
 * @public
 */
export function tokensToCssVars(theme: Theme): Record<string, string> {
    const vars: Record<string, string> = {};
    for (const [key, value] of Object.entries(theme.colors)) vars[`--${key}`] = value;
    for (const [key, value] of Object.entries(theme.spacing)) vars[`--spacing-${key}`] = `${value}px`;
    for (const [key, value] of Object.entries(theme.fonts)) vars[`--${key}`] = value;
    vars['--colorSurfacePrimaryHover'] = toHoverColor(theme.colors.colorSurfacePrimary, 0.1);
    vars['--colorSurfacePrimaryPress'] = toPressColor(theme.colors.colorSurfacePrimary, 0.2);
    vars['--colorButtonSecondaryHover'] = toHoverColor(theme.colors.colorButtonSecondary, 0.1);
    vars['--colorButtonSecondaryPress'] = toPressColor(theme.colors.colorButtonSecondary, 0.2);
    return vars;
}
